/* eslint-disable  @typescript-eslint/no-explicit-any */
import * as React from "react";
import { ReactNode, useEffect, useState } from "react";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import Box from "@mui/material/Box";
import { AlertColor, Button, CircularProgress, FormControl, FormHelperText, InputAdornment } from "@mui/material";
import DialogActions from "@mui/material/DialogActions";
import Dialog from "@mui/material/Dialog";
import { IBackendImage, useBackendImageService } from "../services/registry/BackendImageService";
import { Notify } from "./Notify";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import { DateTime } from "luxon";
import ListItemButton from "@mui/material/ListItemButton";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import Paper from "@mui/material/Paper";

export interface BackendImagePickerProps {
  visible: boolean;
  dialogTitle: string;
  actionButtonLabel: string;
  onCancel: () => void;
  onAction: (backendImage: IBackendImage) => void;
  children?: ReactNode;
}

export const BackendImagePicker = ({
  visible,
  dialogTitle,
  actionButtonLabel,
  onCancel,
  onAction,
  children,
}: BackendImagePickerProps) => {
  const backendImageService = useBackendImageService();

  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");
  const [backendImages, setBackendImages] = useState<IBackendImage[]>([]);
  const [filteredBackendImages, setFilteredBackendImages] = useState<IBackendImage[]>([]);
  const [backendImageError, setBackendImageError] = useState<string | undefined>(undefined);
  const [selectedItem, setSelectedItem] = useState<IBackendImage | undefined>(undefined);
  const [searchValue, setSearchValue] = useState<string>("");

  useEffect(() => {
    (async () => fetchBackendImages())();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const searchValueTrimmed = searchValue.trim().toLowerCase();

    if (searchValueTrimmed.length > 2) {
      setFilteredBackendImages(
        backendImages
          .sort((a, b) => b.creationDate - a.creationDate)
          .filter((img) => {
            return (
              img.name.toLowerCase().includes(searchValueTrimmed) ||
              img.id.toLowerCase().includes(searchValueTrimmed) ||
              img.branch.toLowerCase().includes(searchValueTrimmed) ||
              (img.commitHash && img.commitHash.toLowerCase().includes(searchValueTrimmed)) ||
              (img.imageDigest && img.imageDigest.toLowerCase().includes(searchValueTrimmed))
            );
          }),
      );
    } else {
      const seen = new Set<string>();
      setFilteredBackendImages(
        backendImages
          .sort((a, b) => b.creationDate - a.creationDate)
          .filter((img) => (seen.has(img.name) ? false : seen.add(img.name))),
      );
    }
  }, [searchValue, backendImages]);

  const fetchBackendImages = async () => {
    try {
      const res = await backendImageService.listBackendImages();
      const list = res.list as IBackendImage[];
      setBackendImages(list);
    } catch (e: any) {
      setAlertMessage(e.debugMessage || "Failed to load backend images.");
      setAlertSeverity("error");
    }
  };

  const actionButtonClickHandler = async () => {
    if (!selectedItem) {
      setBackendImageError("You must select a Backend Image");
      return;
    } else {
      setLoading(true);
      setBackendImageError(undefined);
      await onAction(selectedItem);
      setLoading(false);
    }
  };

  return (
    <>
      <Dialog open={visible} fullWidth maxWidth="md">
        <DialogTitle>{dialogTitle}</DialogTitle>
        <DialogContent>
          <Box component="form" sx={{ display: "flex", flexWrap: "wrap" }}>
            {loading ? (
              <CircularProgress />
            ) : (
              <FormControl sx={{ width: "100%" }}>
                <TextField
                  id="search-input"
                  label="Search"
                  variant="standard"
                  value={searchValue}
                  onChange={(event) => setSearchValue(event.target.value)}
                  style={{ marginBottom: 12, width: "100%" }}
                  slotProps={{
                    input: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <SearchIcon />
                        </InputAdornment>
                      ),
                    },
                  }}
                />
                <Paper elevation={0} style={{ maxHeight: 300, overflow: "auto" }}>
                  <List dense disablePadding>
                    {filteredBackendImages.map((img) => {
                      const creationDate = DateTime.fromSeconds(img.creationDate).toLocaleString(
                        DateTime.DATETIME_SHORT_WITH_SECONDS,
                      );
                      return (
                        <ListItem key={img.id} value={img.id} disableGutters>
                          <ListItemButton
                            selected={selectedItem !== undefined && selectedItem.id === img.id}
                            onClick={() => setSelectedItem(img)}
                          >
                            <ListItemText primary={img.name} secondary={`[${img.id}] ${creationDate}`} />
                          </ListItemButton>
                        </ListItem>
                      );
                    })}
                  </List>
                </Paper>
                {backendImageError && <FormHelperText error={true}>{backendImageError}</FormHelperText>}
                {children}
              </FormControl>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={onCancel} color="error" disabled={loading}>
            Cancel
          </Button>
          <Button variant="outlined" onClick={actionButtonClickHandler} disabled={loading}>
            {actionButtonLabel}
          </Button>
        </DialogActions>
      </Dialog>
      {alertMessage && <Notify severity={alertSeverity} message={alertMessage} />}
    </>
  );
};
